import { useState } from 'react';
import type { ReactNode } from 'react';
import useDebounce from 'react-use/esm/useDebounce';
import useMeasure from 'react-use/esm/useMeasure';
import {
  Alert,
  Flex,
  SearchAutocomplete,
  SearchAutocompleteItem,
  Text,
} from '@backstage/ui';
import {
  SearchContextProvider,
  useSearch,
} from '@backstage/plugin-search-react';
import type {
  ResultHighlight,
  SearchDocument,
} from '@backstage/plugin-search-common';
import { ENTITY_TYPE_DOCUMENTATION } from '@internal/plugin-neo4j-techdocs-common';
import { splitHighlight } from '../utils/splitHighlight';
import styles from './DocsHomeSearch.module.css';

const SEARCH_LABEL = 'Search all docs';
const SEARCH_DEBOUNCE_MS = 150;
const RESULT_LIMIT = 30;

/** The fields that the TechDocs collator adds to a search document. */
type DocsSearchDocument = SearchDocument & {
  name?: string;
  entityTitle?: string;
};

/**
 * ADRs and component playbooks belong to their parent component's docs site.
 * The documentation-type filter excludes those sites.
 */
export function DocsHomeSearchProvider({ children }: { children: ReactNode }) {
  return (
    <SearchContextProvider
      initialState={{
        term: '',
        types: ['techdocs'],
        filters: { kind: 'component', type: ENTITY_TYPE_DOCUMENTATION },
        pageLimit: RESULT_LIMIT,
      }}
    >
      {children}
    </SearchContextProvider>
  );
}

/** Requires DocsHomeSearchProvider. */
export function DocsHomeSearch() {
  const {
    term,
    setTerm,
    result: { loading, value },
  } = useSearch();
  const [inputValue, setInputValue] = useState('');
  // The search context runs a query on every term change, so it gets the
  // term only after a pause in typing.
  useDebounce(() => setTerm(inputValue), SEARCH_DEBOUNCE_MS, [inputValue]);
  // Until the term catches up, the results belong to an older input.
  const isLoading = loading || term !== inputValue;
  // The context also runs a query for the empty term, on the first render.
  const results = inputValue && term ? (value?.results ?? []) : [];

  // BUI sizes the popover to its content, so measure the field to match its width.
  const [measureRef, { width: fieldWidth }] = useMeasure<HTMLDivElement>();

  return (
    <div ref={measureRef} className={styles.root}>
      <SearchAutocomplete
        aria-label={SEARCH_LABEL}
        placeholder={SEARCH_LABEL}
        size="medium"
        inputValue={inputValue}
        onInputChange={setInputValue}
        isLoading={isLoading}
        popoverWidth={fieldWidth ? `${fieldWidth}px` : undefined}
      >
        {results.map(({ document, highlight }) => (
          <SearchAutocompleteItem
            key={document.location}
            id={document.location}
            href={document.location}
            textValue={document.title}
          >
            <SearchResult
              document={document as DocsSearchDocument}
              highlight={highlight}
            />
          </SearchAutocompleteItem>
        ))}
      </SearchAutocomplete>
    </div>
  );
}

/**
 * The search box shows "No results found." also when the search fails, so the
 * failure shows here. Needs `DocsHomeSearchProvider`.
 */
export function DocsHomeSearchError() {
  const {
    term,
    result: { error },
  } = useSearch();

  // Suppress errors from the context's automatic empty-term query.
  if (!term || !error) {
    return null;
  }

  return (
    <Alert
      status="danger"
      icon
      title="Could not search the docs"
      description={error.message}
    />
  );
}

function SearchResult({
  document,
  highlight,
}: {
  document: DocsSearchDocument;
  highlight?: ResultHighlight;
}) {
  const siteTitle = document.entityTitle ?? document.name;

  return (
    <Flex direction="column" gap="1">
      <Text variant="body-medium" weight="bold">
        {document.title}
      </Text>
      {siteTitle && siteTitle !== document.title && (
        <Text variant="body-small" color="secondary">
          {siteTitle}
        </Text>
      )}
      <Excerpt text={document.text} highlight={highlight} />
    </Flex>
  );
}

function Excerpt({
  text,
  highlight,
}: {
  text: string;
  highlight?: ResultHighlight;
}) {
  const highlightedText = highlight?.fields.text;
  if (!highlightedText && !text) {
    return null;
  }

  return (
    <Text variant="body-small" color="secondary" className={styles.excerpt}>
      {highlight && highlightedText
        ? splitHighlight(
            highlightedText,
            highlight.preTag,
            highlight.postTag,
          ).map((part, index) =>
            part.isMatch ? <mark key={index}>{part.text}</mark> : part.text,
          )
        : text}
    </Text>
  );
}
